/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import { parseApiDate } from 'src/utils/taskDateUtils';
import {
    Box,
    SwipeableDrawer,
    Typography,
    IconButton,
    Button,
    Stack,
    Skeleton,
    Tooltip,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { dispatch, useSelector } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import {
    getTask,
    getComments,
    setComments,
    completeTask,
    reopenTask,
    deleteTask,
    restoreTask,
    setCurrentTask,
    pauseTask,
    resumeTask,
} from 'src/redux/slices/taskSlice';
import PauseTaskDialog from 'src/components/dialogPopUp/PauseTaskDialog';
import { error, warning, grey, primary } from 'src/theme/palette';
import {
    headerActionButtonStyle,
    headerSeparatorStyle,
} from 'src/styles/taskStyles';
import TaskLayoutSwitcher from './TaskLayoutSwitcher';
import { TASK_LAYOUT_MODES } from './useTaskLayoutMode';
import TaskBodyView from './TaskBodyView';

// -----------------------------------------------

function SlideoutSkeleton() {
    return (
        <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 2, mb: 1.5 }} />
            <Skeleton variant="text" width="75%" height={28} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="50%" height={20} sx={{ mb: 2.5 }} />
            <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 2 }} />
            </Stack>
            <Skeleton variant="text" width="30%" height={18} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={6} sx={{ borderRadius: 3, mb: 1 }} />
            {[1, 2, 3].map((i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                    <Skeleton variant="circular" width={22} height={22} />
                    <Skeleton variant="text" width="60%" height={18} />
                </Stack>
            ))}
            <Skeleton variant="text" width="30%" height={18} sx={{ mt: 2.5, mb: 1 }} />
            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
        </Box>
    );
}

// -----------------------------------------------

export default function TaskSlideout({
    open,
    onClose,
    taskId,
    practiceId,
    onOpenFullView,
    onTaskUpdated,
    // onDeleteChecklist preserved on the API for backwards compat with parents
    // but no longer used internally — body owns checklist mutation.
    // eslint-disable-next-line no-unused-vars
    onDeleteChecklist,
    mode,
    onChangeMode,
    taskList,
    onNavigateTask,
}) {
    const { currentTask, isCurrentTaskLoading } = useSelector((state) => state.task);
    const navigate = useNavigate();
    const theme = useTheme();
    // Narrow viewport: collapse labeled action buttons to icon-only
    const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

    // #226 — Notes / treatment-row / property editor state moved into TaskBodyView.
    const [actionLoading, setActionLoading] = useState(null); // 'complete' | 'reopen' | 'delete'
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [loadError, setLoadError] = useState(null); // 'not_found' | 'network'
    // Tick used by the paused-task countdown ("resumes in 2h 14m"). Updated
    // every 60s and cleared on unmount so the interval doesn't outlive the
    // component (previously caused "setState on unmounted" + Invalid Date
    // crashes when paused tasks were closed mid-render).
    // eslint-disable-next-line no-unused-vars
    const [nowTick, setNowTick] = useState(() => Date.now());

    // Fetch task and comments when taskId changes
    useEffect(() => {
        if (open && taskId && practiceId) {
            setLoadError(null);
            dispatch(setComments(null)); // clear stale comments from previous task
            dispatch(getTask(practiceId, taskId)).then((result) => {
                if (result?.notFound) setLoadError('not_found');
                else if (result?.networkError || result?.error) setLoadError('network');
            });
            dispatch(getComments(practiceId, taskId));
        }
        if (!open) {
            // Reset local state on close
            setActionLoading(null);
            setLoadError(null);
        }
    }, [open, taskId, practiceId]);

    // #226 — Notes + assignee state moved to TaskBodyView. Slideout keeps the
    // notes/assignee local state declared above (currently dead) for header-
    // related diffing only — the body owns rendering and persistence now.

    // Clean up currentTask on unmount
    useEffect(() => () => {
        dispatch(setCurrentTask(null));
    }, []);

    // TODO(tasks-v2): paused at task level not yet implemented — see review H-3.
    // The original countdown ticker hooked off currentTask.resume_at and a
    // 'paused' status that V2 never emits. Keeping the comment + nowTick as
    // a no-op so the ticker can be reinstated wholesale once V2 grows a
    // real paused state.
    // useEffect(() => {
    //     const status = currentTask?.status;
    //     if (!open || status !== 'paused' || !currentTask?.resume_at) return undefined;
    //     const id = setInterval(() => setNowTick(Date.now()), 60_000);
    //     return () => clearInterval(id);
    // }, [open, currentTask?.status, currentTask?.resume_at]);

    // Keyboard shortcuts: C=complete, Esc=close, Cmd/Ctrl+Z=undo.
    // Use ref since handlers are defined below this hook.
    const controlsRef = useRef({ handleComplete: null, handleUndo: null, undoOpen: false, onClose });
    controlsRef.current.onClose = onClose;

    const task = currentTask && currentTask.id === taskId ? currentTask : null;
    const isTaskLoading = isCurrentTaskLoading || (!task && !loadError);

    // Derived data — slideout chrome only needs the bits used by the header
    // action bar / status banner / keyboard shortcuts. Full body-state lives
    // inside TaskBodyView.
    const checklist = task?.checklist_items || task?.checklist || [];
    const status = task?.status || 'pending';
    const isCompleted = status === 'completed';
    const isArchived = status === 'archived';
    const isPaused = status === 'paused';
    const isOverdue = task?.is_overdue || (task?.due_at && !isCompleted && !isArchived && !isPaused && new Date(parseApiDate(task.due_at)) < new Date());
    // TODO(tasks-v2): paused at task level not yet implemented — see review H-3.
    // V1's depends_on field used to be a nested object; the V2 serializer
    // exposes it as a UUID string (or omits it entirely), so the previous
    // `dep.status` read was always undefined and `depIsBlocking` always false.
    // Drop both — the keyboard shortcut `c` no longer needs to gate on it.
    // BE refuses to complete the task if any required checklist item is still PENDING or PAUSED.
    // Compute count locally so we can disable the Complete button before firing the request.
    const pendingRequiredCount = checklist.filter((it) => {
        if (!it.is_required) return false;
        const state = it.completion_state || (it.is_completed || it.completed ? 'YES' : 'PENDING');
        return state === 'PENDING' || state === 'PAUSED';
    }).length;
    const checklistIsBlocking = pendingRequiredCount > 0;
    // Primary action item detection moved into TaskBodyView — slideout no
    // longer needs it (only used to render the body's quick-reply card).

    // ---- Prev/Next task navigation (#180) ----
    // Computes the neighbouring task IDs in the parent view's task list, then
    // honours the active filter by skipping completed tasks (matching the BE
    // default-filter semantics so users on the active tab don't get bounced
    // into a completed task they wouldn't otherwise see).
    const navIds = (() => {
        if (!Array.isArray(taskList) || !taskId) return { prev: null, next: null };
        const ids = taskList.map((t) => (typeof t === 'object' ? String(t.id) : String(t)));
        const idx = ids.findIndex((id) => id === String(taskId));
        if (idx === -1) return { prev: null, next: null };
        // Tasks visible in the current view are already filtered by the parent's
        // queries (e.g. state=active hides completed). We still skip neighbours
        // whose row carries a `status === 'completed'` flag — covers the case
        // where a user is on the "all" tab.
        const isHidden = (t) => {
            if (!t || typeof t !== 'object') return false;
            return t.status === 'completed' && task?.status !== 'completed';
        };
        let prev = null;
        for (let i = idx - 1; i >= 0; i -= 1) {
            if (!isHidden(taskList[i])) { prev = ids[i]; break; }
        }
        let next = null;
        for (let i = idx + 1; i < ids.length; i += 1) {
            if (!isHidden(taskList[i])) { next = ids[i]; break; }
        }
        return { prev, next };
    })();

    const handleNavigatePrev = () => {
        if (navIds.prev && onNavigateTask) onNavigateTask(navIds.prev);
    };
    const handleNavigateNext = () => {
        if (navIds.next && onNavigateTask) onNavigateTask(navIds.next);
    };

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            // I3 fix: tighten the interactive-element guard. The previous check
            // missed combobox/listbox/menu popups (autocomplete/select dropdowns)
            // and nested dialogs — pressing j/k inside an open Autocomplete
            // would still navigate tasks. We also want to ignore keys coming
            // from any non-slideout dialog (e.g. PauseTaskDialog), but NOT the
            // slideout's own dialog (marked with data-task-slideout).
            const ae = document.activeElement;
            const isField =
                ae?.matches?.('input, textarea, [contenteditable="true"]') ||
                !!ae?.closest?.('[role="combobox"], [role="listbox"], [role="menu"], [role="dialog"]:not([data-task-slideout])');
            // Cmd/Ctrl+Z → undo last action (works even inside fields when undo snackbar is up).
            if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z') && !e.shiftKey) {
                if (controlsRef.current.undoOpen) {
                    e.preventDefault();
                    controlsRef.current.handleUndo?.();
                }
                return;
            }
            if (isField) return;
            // Ignore bare-letter shortcuts when ANY non-shift modifier is held.
            // Cmd+C to copy the selected lead name was accidentally triggering
            // "press c to complete" — same trap for Cmd+J / Cmd+K (browser
            // jump-to-search / focus-search) and Alt-combos used by screen
            // readers. The Cmd/Ctrl+Z undo branch above handles its own
            // modifier check + return, so we're safe to gate everything else
            // here.
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === 'Escape') { controlsRef.current.onClose?.(); return; }
            // Vim-style next/prev task — only when the slideout/modal is open.
            // Not bound to any modifier so they're cheap to hit; suppressed
            // when typing in any input (handled above).
            if (e.key === 'j' || e.key === 'J') {
                if (navIds.next) { e.preventDefault(); handleNavigateNext(); }
                return;
            }
            if (e.key === 'k' || e.key === 'K') {
                if (navIds.prev) { e.preventDefault(); handleNavigatePrev(); }
                return;
            }
            if ((e.key === 'c' || e.key === 'C') && !isCompleted && !isArchived && !checklistIsBlocking) {
                controlsRef.current.handleComplete?.();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, isCompleted, isArchived, checklistIsBlocking, navIds.prev, navIds.next]);

    const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
    const [pauseLoading, setPauseLoading] = useState(false);
    const [liveMessage, setLiveMessage] = useState('');
    // #229 — `kind` selects how Undo restores: 'reopen' (after Complete), 'restore' (after Delete), 'item' (after item action).
    // `undoFn` is a callback for item-level undo; null means Undo button is shown but disabled.
    const [undoSnackbar, setUndoSnackbar] = useState({ open: false, message: '', undoTaskId: null, kind: 'reopen', undoFn: null });

    const lead = task?.lead;

    // task.lead can be a nested object {id, ...} or a bare numeric/string FK
    // depending on which endpoint returned the task. Normalise to a numeric ID.
    const resolvedLeadId = (() => {
        if (!task) return null;
        if (lead && typeof lead === 'object' && lead.id) return lead.id;
        if (typeof lead === 'number') return lead;
        if (typeof lead === 'string' && /^\d+$/.test(lead)) return Number(lead);
        return null;
    })();

    // Task may not carry an explicit lead_treatment. Fall back to the lead's
    // most-recently-updated active treatment so the LeadActionZone (rendered
    // inside TaskBodyView) still has something to dispatch against.
    const leadTreatment = task?.lead_treatment
        || (Array.isArray(task?.lead?.lead_treatments) && task.lead.lead_treatments.length > 0
            ? [...task.lead.lead_treatments].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0]
            : null);

    // ---- handlers ----

    const handleRefresh = () => {
        if (taskId && practiceId) {
            dispatch(getTask(practiceId, taskId));
        }
        if (onTaskUpdated) onTaskUpdated();
    };

    // #226 — Dep / notes / assignee / patch-field handlers moved into TaskBodyView.

    const handleComplete = () => {
        setActionLoading('complete');
        dispatch(
            completeTask(practiceId, taskId, ({ success }) => {
                if (success) {
                    setLiveMessage('Task marked complete');
                    setUndoSnackbar({ open: true, message: 'Task completed', undoTaskId: taskId, kind: 'reopen' });
                    onClose();
                    if (onTaskUpdated) onTaskUpdated();
                }
                setActionLoading(null);
            })
        );
    };
    controlsRef.current.handleComplete = handleComplete;

    const handleReopen = () => {
        setActionLoading('reopen');
        dispatch(
            reopenTask(practiceId, taskId, ({ success }) => {
                if (success) {
                    setLiveMessage('Task reopened');
                    handleRefresh();
                    onClose();
                }
                setActionLoading(null);
            })
        );
    };

    const handleDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        setActionLoading('delete');
        const idForUndo = taskId;
        dispatch(
            deleteTask(practiceId, taskId, ({ success }) => {
                if (success) {
                    // #229 — surface an Undo snackbar that calls /restore/ within
                    // the 30-day soft-delete window (see restoreTask thunk).
                    setUndoSnackbar({ open: true, message: 'Task deleted', undoTaskId: idForUndo, kind: 'restore' });
                    onClose();
                    if (onTaskUpdated) onTaskUpdated();
                }
                setActionLoading(null);
            })
        );
    };

    const handlePauseConfirm = (resumeAt) => {
        setPauseLoading(true);
        dispatch(pauseTask(practiceId, taskId, { resumeAt }, () => {
            setPauseLoading(false);
            setPauseDialogOpen(false);
            handleRefresh();
            if (onTaskUpdated) onTaskUpdated();
        }));
        // On error the thunk dispatches updateError; reset loading in any case
        setTimeout(() => setPauseLoading(false), 8000);
    };

    const handleResumeNow = () => {
        dispatch(resumeTask(practiceId, taskId, () => {
            handleRefresh();
            if (onTaskUpdated) onTaskUpdated();
        }));
    };

    const handleUndo = () => {
        const tid = undoSnackbar.undoTaskId;
        const kind = undoSnackbar.kind;
        const fn = undoSnackbar.undoFn;
        setUndoSnackbar((s) => ({ ...s, open: false }));
        if (kind === 'item') {
            if (fn) fn();
            return;
        }
        if (!tid || !practiceId) return;
        if (kind === 'restore') {
            dispatch(
                restoreTask(practiceId, tid, ({ success }) => {
                    if (success && onTaskUpdated) onTaskUpdated();
                })
            );
        } else {
            dispatch(
                reopenTask(practiceId, tid, ({ success }) => {
                    if (success) dispatch(updateError({ success: true, message: 'Task restored' }));
                    if (onTaskUpdated) onTaskUpdated();
                })
            );
        }
    };

    // #289 — called by ChecklistItemRow after item-level actions (e.g. mark-as-sent).
    // undoFn is an async callback; when null the Undo button renders but stays disabled.
    const pushItemUndo = useCallback(({ message, undoFn }) => {
        setUndoSnackbar({ open: true, message, undoTaskId: null, kind: 'item', undoFn: undoFn || null });
    }, []);

    // Keep the keyboard handler ref in sync with the latest undo state + handler.
    controlsRef.current.handleUndo = handleUndo;
    controlsRef.current.undoOpen = undoSnackbar.open;

    const noop = () => {};

    // Outer chrome switch — Drawer (sidebar) vs centered Dialog (modal).
    // The body tree is identical across modes; only the wrapper differs so
    // the status banner / Properties grid / Comments + Attachments render
    // pixel-identically and the LeadActionZone gating matrix keeps holding (#178).
    const isModal = mode === 'modal';

    const renderBody = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Screen-reader live region for task state changes */}
            <Box
                role="status"
                aria-live="polite"
                aria-atomic="true"
                sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}
            >
                {liveMessage}
            </Box>
            {/* Top accent line — matches production */}
            <Box sx={{ height: 3, background: `linear-gradient(90deg, ${primary.main} 0%, #00C897 50%, ${primary.light} 100%)` }} />

            {/* Header action bar — #219 ClickUp-style split:
                LEFT cluster  = navigation/contextual (prev/next, copy id)
                RIGHT cluster = actions (complete, pause, lead, delete, layout, close)
                Generous gaps between groups, icon-only for secondary actions. */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.5,
                py: 0.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
                gap: 1,
                minHeight: 40,
            }}>
                {/* LEFT cluster — Mark complete (primary, with label) + nav chevrons */}
                <Stack direction="row" alignItems="center" spacing={1}>
                    {task && (
                        <Tooltip title={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}>
                            <Button
                                size="small"
                                role="checkbox"
                                aria-checked={isCompleted}
                                aria-label={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                                onClick={() => { if (isCompleted) handleReopen(); else handleComplete(); }}
                                startIcon={
                                    <Box sx={{
                                        width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                                        border: isCompleted ? 'none' : `2px solid ${alpha(primary.main, 0.5)}`,
                                        bgcolor: isCompleted ? primary.main : 'transparent',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        transition: 'all 0.2s ease',
                                    }}>
                                        {isCompleted && <Iconify icon="mdi:check" width={12} sx={{ color: '#fff' }} />}
                                    </Box>
                                }
                                sx={{
                                    ...headerActionButtonStyle,
                                    color: isCompleted ? 'text.disabled' : 'primary.main',
                                    '&:hover': { bgcolor: alpha(primary.main, 0.08) },
                                }}
                            >
                                {isNarrow ? null : (isCompleted ? 'Mark incomplete' : 'Mark complete')}
                            </Button>
                        </Tooltip>
                    )}
                    {/* Prev / Next task navigation — j/k keyboard shortcuts mirror */}
                    {Array.isArray(taskList) && taskList.length > 1 && (
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                            <Tooltip title="Previous task (k)">
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Previous task"
                                        disabled={!navIds.prev}
                                        onClick={handleNavigatePrev}
                                        sx={{ color: 'text.secondary', '&:hover': { color: primary.main, bgcolor: alpha(primary.main, 0.08) } }}
                                    >
                                        <Iconify icon="eva:chevron-left-fill" width={18} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Next task (j)">
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Next task"
                                        disabled={!navIds.next}
                                        onClick={handleNavigateNext}
                                        sx={{ color: 'text.secondary', '&:hover': { color: primary.main, bgcolor: alpha(primary.main, 0.08) } }}
                                    >
                                        <Iconify icon="eva:chevron-right-fill" width={18} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>

                {/* RIGHT cluster — secondary actions + chrome */}
                <Stack direction="row" alignItems="center" spacing={1}>
                    {/* Secondary icon group — Pause + Lead, icon-only with tooltip (#219) */}
                    <Stack direction="row" alignItems="center" spacing={0.25}>
                        {task && !isCompleted && !isArchived && (
                            <Tooltip title={isPaused ? 'Resume task' : 'Pause task'}>
                                <IconButton
                                    size="small"
                                    aria-label={isPaused ? 'Resume task' : 'Pause task'}
                                    onClick={isPaused ? handleResumeNow : () => setPauseDialogOpen(true)}
                                    sx={{ color: 'warning.dark', '&:hover': { bgcolor: alpha(warning.main, 0.10) } }}
                                >
                                    <Iconify icon={isPaused ? 'eva:play-circle-outline' : 'eva:pause-circle-outline'} width={18} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {task && resolvedLeadId && (
                            <Tooltip title="Open lead detail">
                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<Iconify icon="eva:external-link-fill" width={16} />}
                                    onClick={() => { onClose(); navigate(`/dashboard/practice/${practiceId}/lead-detail/${resolvedLeadId}`); }}
                                    sx={{
                                        color: 'text.secondary',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.8rem',
                                        px: 1,
                                        py: 0.5,
                                        minWidth: 0,
                                        '&:hover': { color: primary.main, bgcolor: alpha('#00A76F', 0.08) },
                                    }}
                                >
                                    Go to lead
                                </Button>
                            </Tooltip>
                        )}
                    </Stack>

                    {/* Thin vertical divider before destructive + chrome group */}
                    <Box sx={headerSeparatorStyle} />

                    {/* Delete — icon-only with tooltip (#219) */}
                    <Tooltip title="Delete task">
                        <IconButton
                            size="small"
                            aria-label="Delete task"
                            onClick={() => setDeleteConfirmOpen(true)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: alpha(error.main, 0.08) } }}
                        >
                            <Iconify icon="eva:trash-2-outline" width={18} />
                        </IconButton>
                    </Tooltip>

                    {/* Layout switcher (#179) — Modal · Full screen · Sidebar */}
                    {onChangeMode && (
                        <TaskLayoutSwitcher
                            mode={mode}
                            onChange={(next) => {
                                if (next === TASK_LAYOUT_MODES.FULL && onOpenFullView) {
                                    onOpenFullView(taskId);
                                    return;
                                }
                                onChangeMode(next);
                            }}
                        />
                    )}

                    {/* Close */}
                    <Tooltip title="Close">
                        <IconButton
                            size="small"
                            aria-label="Close task panel"
                            onClick={onClose}
                            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
                        >
                            <Iconify icon="eva:close-fill" width={20} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {/* V1-style status strip — single dominant state banner directly
                below the header action row, above the title. Mirrors V1
                TaskSidebar.jsx:293 (overdue red) + paused amber + completed
                green + neutral active. Replaces the old chip-row Status pill
                and the standalone Paused banner that lived inside the body. */}
            {task && (() => {
                const palette =
                    isCompleted ? { bg: alpha(primary.main, 0.08), border: primary.main, fg: primary.dark, icon: 'mdi:check-circle' } :
                    isArchived  ? { bg: alpha(grey[500], 0.10), border: grey[600],  fg: grey[800],     icon: 'mdi:archive-outline' } :
                    isPaused    ? { bg: alpha(warning.main, 0.10), border: warning.main, fg: 'warning.dark', icon: 'eva:pause-circle-fill' } :
                    isOverdue   ? { bg: '#fef2f2', border: '#dc2626', fg: '#dc2626', icon: 'mdi:alert' } :
                                  { bg: alpha(primary.main, 0.06), border: primary.main, fg: primary.dark, icon: 'eva:radio-button-on-fill' };
                // TODO(tasks-v2): paused at task level not yet implemented —
                // see review H-3. V2 has no `resume_at` field on the Task
                // model and the BE never emits status='paused' at the task
                // level, so the previous countdown / resume timestamp logic
                // was unreachable. Render a static "Paused" label until V2
                // grows a real paused state.
                const pausedLabel = 'Paused';
                const label =
                    isCompleted ? 'Completed' :
                    isArchived  ? 'Archived' :
                    isPaused    ? pausedLabel :
                    isOverdue   ? 'Overdue' :
                                  'Active';
                return (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 1, px: 2, py: 0.75,
                        bgcolor: palette.bg,
                        borderLeft: `3px solid ${palette.border}`,
                        borderBottom: '1px solid', borderColor: 'divider',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Iconify icon={palette.icon} width={14} sx={{ color: palette.fg, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: palette.fg, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.3 }} noWrap>
                                {label}
                            </Typography>
                        </Box>
                        {isPaused && (
                            <Button
                                size="small"
                                variant="text"
                                onClick={handleResumeNow}
                                sx={{ textTransform: 'none', fontSize: '11px', color: 'warning.dark', fontWeight: 700, p: '2px 6px', minWidth: 0, '&:hover': { bgcolor: alpha(warning.main, 0.15) } }}
                            >
                                Resume now
                            </Button>
                        )}
                    </Box>
                );
            })()}

            {/* Content area — #316 px tightened from 2.5 to 1.5 to reclaim ~16px of horizontal content space */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pt: 2 }}>
                {isTaskLoading && <SlideoutSkeleton />}
                {!isTaskLoading && loadError === 'network' && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                            Failed to load task. Please try again.
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                setLoadError(null);
                                dispatch(getTask(practiceId, taskId));
                            }}
                        >
                            Retry
                        </Button>
                    </Box>
                )}
                {!isTaskLoading && !loadError && !task && (
                    <Typography color="text.secondary">Task not found</Typography>
                )}
                {!isTaskLoading && !loadError && task && (
                    <TaskBodyView
                        task={task}
                        lead={lead}
                        leadTreatment={leadTreatment}
                        practiceId={practiceId}
                        taskId={taskId}
                        mode={isModal ? 'modal' : 'sidebar'}
                        open={open}
                        onTaskUpdated={onTaskUpdated}
                        onClose={onClose}
                        nowTick={nowTick}
                        pushItemUndo={pushItemUndo}
                    />
                )}
            </Box>

        </Box>
    );

    return (
        <>
        {isModal ? (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                fullScreen={isNarrow}
                PaperProps={{
                    // data-task-slideout marks this as the slideout's own
                    // dialog so the j/k/c/Esc keydown guard doesn't suppress
                    // shortcuts that originate inside it (I3 fix).
                    'data-task-slideout': 'true',
                    sx: {
                        width: { xs: '100%', md: 720 },
                        maxWidth: { xs: '100%', md: 720 },
                        height: { xs: '100%', md: '85vh' },
                        m: { xs: 0, md: 2 },
                        borderRadius: { xs: 0, md: 2 },
                        overflow: 'hidden',
                        backgroundImage: 'none !important',
                    },
                }}
                sx={{ zIndex: 1116, '& .MuiDialog-container': { alignItems: { md: 'center' } } }}
            >
                {renderBody()}
            </Dialog>
        ) : (
            <SwipeableDrawer
                anchor="right"
                open={open}
                onClose={onClose}
                onOpen={noop}
                BackdropProps={{ invisible: true }}
                PaperProps={{
                    sx: {
                        width: '420px',
                        maxWidth: '85%',
                        backgroundImage: 'none !important',
                        borderLeft: 'none',
                        boxShadow: '-8px 0 24px rgba(0,0,0,0.08)',
                    },
                }}
                sx={{ zIndex: 1116 }}
            >
                {renderBody()}
            </SwipeableDrawer>
        )}
        <PauseTaskDialog
            open={pauseDialogOpen}
            onClose={() => setPauseDialogOpen(false)}
            onConfirm={handlePauseConfirm}
            loading={pauseLoading}
        />
        <Snackbar
            open={undoSnackbar.open}
            autoHideDuration={5000}
            onClose={() => setUndoSnackbar((s) => ({ ...s, open: false }))}
            message={undoSnackbar.message}
            action={
                <Button
                    size="small"
                    color="inherit"
                    onClick={handleUndo}
                    disabled={undoSnackbar.kind === 'item' && undoSnackbar.undoFn === null}
                    sx={{ fontWeight: 700 }}
                >
                    Undo
                </Button>
            }
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Delete task?</DialogTitle>
            <DialogContent>
                <DialogContentText>This cannot be undone.</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button
                    onClick={handleDeleteConfirm}
                    disabled={actionLoading === 'delete'}
                    color="error"
                    variant="contained"
                    sx={{ textTransform: 'none' }}
                >
                    {actionLoading === 'delete' ? 'Deleting…' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}

TaskSlideout.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    taskId: PropTypes.string,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onOpenFullView: PropTypes.func,
    onTaskUpdated: PropTypes.func,
    onDeleteChecklist: PropTypes.func,
    // Layout mode: 'sidebar' renders the right-anchored drawer (default);
    // 'modal' renders a centered MUI Dialog with the same body+header.
    // 'full' is handled by the parent (navigate) and never reaches this component.
    mode: PropTypes.oneOf(['sidebar', 'modal']),
    onChangeMode: PropTypes.func,
    // Ordered list of task IDs in the parent view — drives prev/next nav.
    taskList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    // (nextTaskId) => void — called when the user clicks chevron / presses j/k.
    onNavigateTask: PropTypes.func,
};

TaskSlideout.defaultProps = {
    taskId: null,
    onOpenFullView: null,
    onTaskUpdated: null,
    onDeleteChecklist: null,
    mode: 'sidebar',
    onChangeMode: null,
    taskList: null,
    onNavigateTask: null,
};
